import React, { createContext, useState, useContext, ReactNode } from 'react';
import { CartContextType, CartItem, Product, CartOption } from '../types';

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);

  const addToCart = (item: Product & { options?: CartOption[] }) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(
        cartItem => cartItem.id === item.id && 
        JSON.stringify(cartItem.options) === JSON.stringify(item.options)
      );

      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.id === item.id && 
          JSON.stringify(cartItem.options) === JSON.stringify(item.options)
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }

      return [...prevCart, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (itemId: string, options?: CartOption[]) => {
    setCart(prevCart =>
      prevCart.filter(
        item => !(item.id === itemId && JSON.stringify(item.options) === JSON.stringify(options))
      )
    );
  };

  const updateQuantity = (itemId: string, options: CartOption[] | undefined, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId, options);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.id === itemId && JSON.stringify(item.options) === JSON.stringify(options)
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotal = (): number => {
    return cart.reduce((total, item) => {
      const optionsPrice = item.options?.reduce((sum, opt) => sum + (opt.price || 0), 0) || 0;
      return total + (item.price + optionsPrice) * item.quantity;
    }, 0);
  };

  const getItemCount = (): number => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotal,
        getItemCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
